'use strict';

// external imports
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Card} from 'primereact/card';

// shared imports
import metaModel from '~/shared/src/utilities/metaModel';
import {
    Graph,
    GraphType
} from '~/shared/src/utilities/graph';
import {LoadDataForDataStructureAndConditionParameters} from '~/shared/src/utilities/modules/graph';
import {conditionSchema} from '~/shared/src/utilities/condition';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import {extractDataObjectsFromDataStructure} from '~/shared/src/utilities/dataStructure';

// frontend components import
import GraphUI, {
    AllProps as GraphAllProps,
    ElusiveData as GraphElusiveData
} from '~/frontend/src/components/base/Graph';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';

// frontend imports

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import startRequest from '~/frontend/src/actions/ajax/startRequest';
import loadDataFromBackend from '~/frontend/src/actions/data/loadData';
import endRequest from '~/frontend/src/actions/ajax/endRequest';
import updateItem from '~/frontend/src/actions/data/updateItem';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import cancelRequests from '~/frontend/src/actions/ajax/cancelRequests';


export type DefaultProps = {
    readOnly? : boolean;
    additionalElusiveDataContexts? : string[];
    rootItemFormState? : 'CLEAR' | 'DIRTY' | 'ERROR';
};
export const defaultProps : Required<DefaultProps> = {
    readOnly                     : false,
    additionalElusiveDataContexts: [],
    rootItemFormState            : 'CLEAR'
};

export type Props = {
    uid : string;

    dataDataContext : string;

    dataContext : string;

    dataRootItemId? : number;

    rootItemId : number;

    selectedElementId? : number;

    /**
     * Callback if a layout element is selected.
     *
     * @param id
     */
    onSelectElement? : ( id? : number ) => void;
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;


const setParamForGraph       = origSetParam as <K extends keyof GraphAllProps>( panelKey : string, paramKey : K, paramValue : GraphAllProps[K] ) => SetParamAction,
      setElusiveDataForGraph = origSetElusiveData as ( panelKey : string, data : GraphElusiveData ) => SetElusiveDataAction;


export type ReferenceType = {
    graph? : Graph;
};


const GraphDesigner = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const dispatch : AppDispatch = useDispatch(),
          {
              additionalElusiveDataContexts,
              dataContext,
              dataDataContext,
              dataRootItemId,
              onSelectElement,
              readOnly,
              rootItemId,
              rootItemFormState,
              selectedElementId,
              uid
          }                      = props,
          graphDataDataContext   = `${dataDataContext}_graph`,
          allElusiveData         = useSelector( ( state : StoreState ) => state.elusiveData ),
          lastParams             = useRef<any>( null ),
          loadParams             = useRef<string>( '' ),
          loadTimeoutId          = useRef<NodeJS.Timeout | undefined>(),
          globalDataVersions     = useMemo(
              () => {
                  return JSON.stringify( allElusiveData?.[ dataContext ]?.data ?? {} );
              },
              [
                  allElusiveData?.[ dataContext ]?.data
              ]
          ),
          globalDataDataVersions = useMemo(
              () => {
                  return JSON.stringify( allElusiveData?.[ graphDataDataContext ]?.data ?? {} );
              },
              [
                  allElusiveData?.[ graphDataDataContext ]?.data
              ]
          ),
          rootItem               = useMemo(
              () => {
                  return global.data[ dataContext ]?.items[ rootItemId ];
              },
              [
                  dataContext,
                  rootItemId,
                  globalDataVersions
              ]
          ),
          graph                  = useMemo(
              () => {
                  return rootItem ? dataExtraction.extractGraphData( rootItem, dataContext, true ) : undefined;
              },
              [
                  dataContext,
                  rootItem,
                  globalDataVersions
              ]
          ),
          dataSchema             = useMemo(
              () => {
                  return ( graph?.query?.dataStructure?.item.objectSchema ?? 'data' ).toUpperCase() as Schema;
              },
              [
                  graph?.query?.dataStructure?.item.objectSchema
              ]
          ),
          referenceDepth         = useMemo(
              () => {
                  return graph?.query?.dataStructure
                      ? extractDataObjectsFromDataStructure( graph.query.dataStructure ).length - 1
                      : 1;
              },
              [
                  graph?.query?.dataStructure
              ]
          ),
          dataStructures         = useMemo(
              () => {
                  if ( graph?.query?.dataStructure ) {
                      return [ graph.query.dataStructure ];
                  }
                  else {
                      return undefined;
                  }
              },
              [
                  graph?.query?.dataStructure
              ]
          ),
          dataConfig             = useMemo(
              () => {
                  if ( dataRootItemId ) {
                      return {
                          schema    : dataSchema,
                          rootItemId: dataRootItemId
                      };
                  }
                  else {
                      return undefined;
                  }
              },
              [
                  dataSchema,
                  dataRootItemId
              ]
          ),
          dataDisplayConfig      = useMemo(
              () => {
                  return {
                      referenceDirection: 'both',
                      referenceDepth
                  } as GraphAllProps['dataDisplayConfig'];
              },
              [
                  referenceDepth
              ]
          ),
          onGraphClick           = useCallback(
              ( e : React.MouseEvent<HTMLDivElement> ) => {
                  lastParams.current?.onSelectElement?.( undefined );
              },
              [
                  'onlyOnce'
              ]
          ),
          loadData               = async ( reload : boolean = false ) => {
              if ( dataRootItemId && graph?.query?.dataStructure ) {
                  dispatch( startRequest(
                      () => {
                      },
                      uid,
                      'processing'
                  ) );

                  let conditionValid = true;

                  // validate condition to avoid backend error
                  if ( graph.condition?.id ) {
                      const conditionValidationResult = conditionSchema.validate(
                          graph.condition,
                          {
                              abortEarly  : false,
                              stripUnknown: true,
                              convert     : false
                          }
                      );

                      if ( conditionValidationResult.error ) {
                          conditionValid = false;
                      }
                      else {
                          try {
                              dataExtraction.extractCondition( rootItem, dataContext, graph.query.dataStructure );
                          }
                          catch ( e ) {
                              conditionValid = false;
                          }
                      }
                  }

                  if ( conditionValid ) {
                      const params : LoadDataForDataStructureAndConditionParameters = {
                                rootItemId   : dataRootItemId,
                                dataStructure: graph.query.dataStructure,
                                condition    : graph.condition
                            },
                            stringifiedParams                                       = JSON.stringify( params );

                      if ( reload || loadParams.current !== stringifiedParams ) {
                          loadParams.current = stringifiedParams;

                          if ( global.data[ graphDataDataContext ] ) {
                              delete global.data[ graphDataDataContext ];
                          }

                          await dispatch(
                              loadDataFromBackend(
                                  'graph/loadDataForDataStructureAndCondition',
                                  graphDataDataContext,
                                  uid,
                                  'loadData',
                                  dataSchema,
                                  {
                                      params,
                                      mergeMode: 'override'
                                  },
                                  undefined,
                                  () => {
                                      dispatch( setParamForGraph( `${uid}_graph`, 'svgGraphTransform', undefined ) );
                                      dispatch( setParamForGraph( `${uid}_graph`, 'svgNodesTransform', undefined ) );
                                  }
                              )
                          );
                      }
                  }

                  dispatch( endRequest(
                      () => {
                      },
                      uid,
                      'processing'
                  ) );
              }
          },
          onReloadGraph          = useCallback(
              () => {
                  lastParams.current?.loadData( true );
              },
              [
                  'onlyOnce'
              ]
          ),
          onGraphTypeChange      = useCallback(
              ( graphType : GraphType ) => {
                  const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'GraphType' )!.id;

                  dispatch( updateItem(
                      dataContext,
                      rootItemId,
                      {
                          properties: {
                              [ propertyTypeId ]: {
                                  value: graphType
                              }
                          }
                      },
                      additionalElusiveDataContexts
                  ) );
              },
              [
                  additionalElusiveDataContexts,
                  dataContext,
                  rootItemId
              ]
          ),
          onShowTooltipsChange   = useCallback(
              ( showTooltips : boolean ) => {
                  const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'ShowTooltips' )!.id;

                  dispatch( updateItem(
                      dataContext,
                      rootItemId,
                      {
                          properties: {
                              [ propertyTypeId ]: {
                                  value: showTooltips
                              }
                          }
                      },
                      additionalElusiveDataContexts
                  ) );
              },
              [
                  additionalElusiveDataContexts,
                  dataContext,
                  rootItemId
              ]
          ),
          onColorItemTypesChange = useCallback(
              ( colorItemTypes : boolean ) => {
                  const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'ColorItemTypes' )!.id;

                  dispatch( updateItem(
                      dataContext,
                      rootItemId,
                      {
                          properties: {
                              [ propertyTypeId ]: {
                                  value: colorItemTypes
                              }
                          }
                      },
                      additionalElusiveDataContexts
                  ) );
              },
              [
                  additionalElusiveDataContexts,
                  dataContext,
                  rootItemId
              ]
          ),
          graphContent           = useMemo(
              () => {
                  let retVal : React.ReactNode = null;

                  if (
                      graph?.query?.dataStructure
                      && dataSchema
                      && dataRootItemId
                      && Object.keys( global.data[ graphDataDataContext ]?.items ?? {} ).length
                  ) {
                      retVal = <LoadingMaskPanel
                          key={`${uid}_graph_loadingMask`}
                          forComponent={`${uid}_graph`}
                          className="yn-view-loadingmask yn-graphview-loadingmask"
                          style={{ width: '100%' }}
                      >
                          <GraphUI
                              key={`${uid}_graph`}
                              uid={`${uid}_graph`}

                              dataContext={dataDataContext}
                              dataStructures={dataStructures}
                              dataConfig={dataConfig}
                              dataDisplayConfig={dataDisplayConfig}

                              viewKey="graphDesigner"
                              showReferenceDirectionFilter={false}
                              showReferenceDepthFilter={false}
                              showOptionsMenu={false}
                              onReloadView={onReloadGraph}
                              onGraphTypeChange={!readOnly ? onGraphTypeChange : undefined}
                              onShowTooltipsChange={!readOnly ? onShowTooltipsChange : undefined}
                              onColorItemTypesChange={!readOnly ? onColorItemTypesChange : undefined}

                              type={graph.data.type}
                              graphType={graph.data.graphType}
                              layout={graph.data.layout}

                              colorItemTypes={graph.data.colorItemTypes}
                              iconNodeOnly={graph.data.iconNodeOnly}
                              showLinkCardinality={graph.data.showLinkCardinality}
                              showLinkNames={graph.data.showLinkNames}
                              showTooltips={graph.data.showTooltips}

                              svgGraphTransform={graph.data.svgGraphTransform}
                              svgNodesTransform={graph.data.svgNodesTransform}

                              nodeLabel={graph.messages?.nodeLabel}
                              nodesLabel={graph.messages?.nodesLabel}
                              linkLabel={graph.messages?.linkLabel}
                              linksLabel={graph.messages?.linksLabel}
                          />
                      </LoadingMaskPanel>;
                  }
                  else if ( graph && !graph.query ) {
                      retVal = <LoadingMaskPanel
                          key={`${uid}_graph_loadingMask`}
                          forComponent={`${uid}_graph`}
                          className="yn-view-loadingmask yn-graphview-loadingmask"
                          style={{ width: '100%' }}
                      >
                          <GraphUI
                              key={`${uid}_graph`}
                              uid={`${uid}_graph`}

                              viewKey={graph.data.key}

                              schema={dataSchema}
                              dataContext={dataDataContext}
                              isGenericGraph={true}
                              dataConfig={dataConfig}

                              onGraphTypeChange={!readOnly ? onGraphTypeChange : undefined}
                              onShowTooltipsChange={!readOnly ? onShowTooltipsChange : undefined}
                              onColorItemTypesChange={!readOnly ? onColorItemTypesChange : undefined}

                              type={'data'}
                              graphType={graph.data.graphType}
                              layout={graph.data.layout}

                              colorItemTypes={graph.data.colorItemTypes}
                              iconNodeOnly={graph.data.iconNodeOnly}
                              showLinkCardinality={graph.data.showLinkCardinality}
                              showLinkNames={graph.data.showLinkNames}
                              showTooltips={graph.data.showTooltips}

                              svgGraphTransform={graph.data.svgGraphTransform}
                              svgNodesTransform={graph.data.svgNodesTransform}

                              nodeLabel={graph.messages?.nodeLabel}
                              nodesLabel={graph.messages?.nodesLabel}
                              linkLabel={graph.messages?.linkLabel}
                              linksLabel={graph.messages?.linksLabel}
                          />
                      </LoadingMaskPanel>;
                  }
                  return retVal;
              },
              [
                  dataConfig,
                  dataDataContext,
                  dataDisplayConfig,
                  dataRootItemId,
                  dataSchema,
                  dataStructures,
                  globalDataDataVersions,
                  graph,
                  graphDataDataContext,
                  onColorItemTypesChange,
                  onGraphTypeChange,
                  onReloadGraph,
                  onShowTooltipsChange,
                  readOnly,
                  uid
              ]
          );

    // expose methods via reference
    useImperativeHandle(
        forwardedRef,
        () => ( {
            graph
        } )
    );

    lastParams.current = {
        loadData,
        onSelectElement
    };

    // ( on mount / unmount )
    // add/remove listener for color scheme switch
    // remove observed element from resizeObserver
    useEffect(
        () => {

            // unmount
            return () => {
                if ( loadTimeoutId.current ) {
                    clearTimeout( loadTimeoutId.current );
                }

                dispatch( cancelRequests( uid ) );
            }
        },
        []
    );

    // init graph
    useEffect(
        () => {
            dispatch( setElusiveDataForGraph( `${uid}_graph`, {
                    configLoaded: graph?.query?.dataStructure
                        ? true
                        : undefined,
                    dataLoaded  : graph?.query?.dataStructure
                        ? true
                        : undefined,
                    data        : {}
                }
            ) );
        },
        [
            graph?.query?.dataStructure
        ]
    );

    // re-init graph for new data rootItemId
    useEffect(
        () => {
            if ( graph && !graph.query ) {
                dispatch( setElusiveDataForGraph( `${uid}_graph`, {
                        dataLoaded: false,
                        data      : {}
                    }
                ) );
                dispatch( setParamForGraph( `${uid}_graph`, 'hiddenNodeItemTypes', [] ) );
                dispatch( setParamForGraph( `${uid}_graph`, 'svgGraphTransform', undefined ) );
                dispatch( setParamForGraph( `${uid}_graph`, 'svgNodesTransform', undefined ) );
                dispatch( setParamForGraph( `${uid}_graph`, 'dataConfig', dataRootItemId
                    ? {
                        schema    : dataSchema,
                        rootItemId: dataRootItemId
                    }
                    : undefined
                ) );
            }
        },
        [
            dataRootItemId
        ]
    );

    // load data (throttled)
    useEffect(
        () => {
            if ( graph?.query?.dataStructure && dataRootItemId ) {
                if ( loadTimeoutId.current ) {
                    clearTimeout( loadTimeoutId.current );
                }
                loadTimeoutId.current = setTimeout(
                    () => {
                        void loadData();
                    },
                    250
                );
            }
        },
        [
            dataRootItemId,
            dataSchema,
            graph?.condition,
            graph?.query?.dataStructure
        ]
    );

    return useMemo(
        () => {
            const classNames = [
                'yn-form-card',
                'yn-view-card',
                'yn-view-card-standalone',
                'yn-view-card-standalone-view'
            ];

            if ( rootItemFormState === 'ERROR' ) {
                classNames.push( 'yn-view-card-border-error' );
            }
            else if ( rootItemFormState === 'DIRTY' ) {
                classNames.push( 'yn-view-card-border-dirty' );
            }
            else if ( selectedElementId === rootItemId ) {
                classNames.push( 'yn-view-card-border-selected' );
            }

            /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
            return <div
                id={`${uid}_view`}
                key={`${uid}_view`}
                className={classNames.join( ' ' )}
                onClick={onGraphClick}
            >
                <Card
                    id={`${uid}_card`}
                    key={`${uid}_card`}
                    style={{ flex: '1' }}
                >
                    <div
                        className="yn-view-wrapper yn-view-wrapper-map yn-view-wrapper-card"
                    >
                        <div
                            id={`${uid}_border`}
                            key={`${uid}_border`}
                            className="yn-view-wrapper-border"
                        >
                            {graphContent}
                        </div>
                    </div>
                </Card>
            </div>;
        },
        [
            graphContent,
            onGraphClick,
            rootItemFormState,
            rootItemId,
            selectedElementId,
            uid
        ]
    );
} );
GraphDesigner.defaultProps = defaultProps;
GraphDesigner.displayName = 'GraphDesigner';

export default GraphDesigner;
